// Angular Core Imports
import { Component, OnInit, Input, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';

// Material Imports
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';

// RxJS Imports
import { Observable, Subscription, map } from 'rxjs';

// Third-Party Imports
import Swal from 'sweetalert2';

// Local Service Imports
import { ProfileService } from '../../../../services/profile.service';
import { ExcelExportService } from '../../../../services/excel-export.service';
import { UserService } from '../../../../services/user';
import { ItemsService } from '../../../../services/items.service';
import { OrdersService } from '../../../../services/orders';
import { FinancialManagerService } from '../../../../services/financial-manager';
import { DeliveryService } from '../../../../services/delivery';

import { echo } from '../../../../services/realtimeorders';

// Local Interface Imports
import { Order } from '../../../../interfaces/order';
import { users } from '../../../../interfaces/users';
import { WarehouseTypeService } from '../../../../services/warehouse-type';

// ==================== CONSTANTS (Extracted Magic Values) ====================
const ORDER_STATUS = {
  PENDING: 1,
  APPROVED: 2,
  REJECTED: 3,
  ON_THE_WAY: 4,
  SHIPPED: 5,
  DEFERRED: 6,
  NOT_CONTACT_CLIENT: 7,
  DELIVERED: 8
} as const;

const DELIVERY_STATUS = {
  WAITING: 0,
  IN_PROGRESS: 1,
  NOT_DELIVERED: 2,
  DELIVERED: 3
} as const;

const PAYMENT_TYPE = {
  CASH: '0',
  VODAFONE_CASH: '1',
  INSTAPAY: '2',
  ONLINE_PAYMENT: '3',
  BANK_TRANSFER: '4',
  CREDIT: '5'
} as const;

const WAREHOUSE_TYPE = {
  COMPANY: 0,
  WAREHOUSE: 1,
  STORE: 2,
  CLINIC: 3,
  STORE_AND_CLINIC: 4
} as const;

const ACCOUNT_TYPE = {
  ADMIN: '1',
  SUPER_ADMIN: '10',
  SUB_ADMIN: '6',
  DELIVERY_HERO: '8'
} as const;

@Component({
  selector: 'app-orderspage',
  templateUrl: './orderspage.component.html',
  styleUrl: './orderspage.component.scss'
})
export class OrderspageComponent implements OnInit, OnDestroy {
  // ==================== INPUTS & CONFIG ====================
  @Input() isProfile: string = '';
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // ==================== UI STATE FLAGS ====================
  contentReady: boolean = true;
  noData: boolean = false;
  showPopUp: boolean = false;
  showRejectPopUp: boolean = false;
  showDiscountPopUp: boolean = false;
  showDeliveryHeroPopUp: boolean = false;
  showCommentPopUp: boolean = false;
  showPaymentPopUp: boolean = false;
  showtodaysorders: boolean = false;
  showQuickInvoice: boolean = false;
  showPartCompPopUp: boolean = false;
  showoptions: boolean = false;
  changeRemaining: boolean = false;
  clear: boolean = false;
  readonly isAr: boolean = localStorage.getItem('language') === 'ar';
  isCompany = false;
  isWarehouse: boolean = false;
  isStore: boolean = false;
  isClinic: boolean = false;
  isStoreAndClinic: boolean = false;

  // ==================== USER & ACCOUNT DATA ====================
  userimage!: string;
  userData!: users;
  username!: string;
  userPhone: string = '';
  userName: string = '';
  profile!: boolean;
  userfirstname: string = '';
  userFacebook: string = '';
  userInstgram: string = '';
  userTiktok: string = '';
  accountType!: string;
  userId!: number;
  warehouseId!: any | null;
  adminStatus!: string;
  isAdmin!: boolean;
  isSubAdmin!: boolean;
  isDeliveryHero!: boolean;
  showAdminContent: boolean = false;

  // ==================== ORDER DATA & TABLE ====================
  allData: Order[] = [];
  selectedDeliveryOrders: any[] = [];
  todaysOrders: any[] = [];
  deliveryHeros!: any[];
  displayedColumns: string[] = [
    'client_info', 'order_details', 'order_meta'
  ];
  dataSource: MatTableDataSource<any>;
  stockTotal!: number;
  totalItems!: number;
  newPage: number = 1;
  searchKey: string = '';
  searchstatus!: number | null;
  searchtype!: string | null;
  searchOrderId!: any | null;
  todaysOrdersIds: any;
  selectedProductsDetails: any[] = [];
  selectedOrderId!: number | null;
  allWareHouses: any[] = [];
  warehouseType!: any;

  // ==================== ACTION STATE ====================
  orderchange!: FormGroup;
  reason: string = '';
  commentOrderId!: any | null;
  DiscountOrderId!: number | null;
  deliveryHeroOrderId!: number | null;
  orderId!: number | null;
  payment_type!: any | null;
  payed!: any;
  not_payed!: any;
  comment!: string | null;
  discount!: string | null;
  total!: any | null;
  income!: string;
  duenow!: any;
  deliveryFees!: any;
  deliveryHero!: any;
  deliveryHeroId!: any | null;
  deliveryHeroInfo!: string | null;
  commentTotal: any;

  // ==================== OBSERVABLES & SUBSCRIPTIONS ====================
  isAdmin$: Observable<boolean>;
  isSubAdmin$: Observable<boolean>;
  private _accountTypeSub?: Subscription;
  private _userIdSub?: Subscription;

  // ==================== AUDIO NOTIFICATION ====================
  private readonly notificationSound = new Audio('assets/sound/order.mp3');

  // ==================== CONSTRUCTOR ====================
  constructor(
    private readonly router: Router,
    private readonly _deliveryhero: DeliveryService,
    private readonly _orderService: OrdersService,
    private readonly _itemsService: ItemsService,
    private readonly _profileService: ProfileService,
    private readonly excelExportService: ExcelExportService,
    private readonly _financialManager: FinancialManagerService,
    private readonly cdr: ChangeDetectorRef,
    private readonly _userService: UserService,
    public warehouseTypeService: WarehouseTypeService,
  ) {
    this.dataSource = new MatTableDataSource(this.allData);
    this.isAdmin$ = this._userService.getAccountType().pipe(
      map(() => this.accountType === ACCOUNT_TYPE.ADMIN || this.accountType === ACCOUNT_TYPE.SUPER_ADMIN)
    );
    this.isSubAdmin$ = this._userService.getAccountType().pipe(
      map(() => this.accountType === ACCOUNT_TYPE.SUB_ADMIN)
    );
  }

  // ==================== LIFECYCLE HOOKS ====================
  ngOnInit(): void {
    this._subscribeToAccountType();
    this._subscribeToUserId();
    this._initializeSelectedDeliveryOrders();
  }

  ngOnDestroy(): void {
    this._unsubscribeFromServices();
    if (this.warehouseId) {
      echo.leave(`orders-warehouse-${this.warehouseId}`);
    }
    this.notificationSound.pause();
    this.notificationSound.src = '';
  }
  checkWarehouseType(): void {
    this._userService
      .getWarehouseType(Number(localStorage.getItem('posId')))
      .subscribe({
        next: ({ warehouses_type }) => {
          const type = Number(warehouses_type);



          // Handle navigation
          if ([2, 3, 4].includes(type)) {
            this.router.navigate(['/Addorderstore']);
            return;
          }

          else if (type === 1) {
            this.router.navigate(['/Addorderwarehouse']);
            return;
          }
          else {
            this.router.navigate(['/Addorder']);
            return;
          }
          // type === 0 أو أي حاجة تانية → مفيش action
        },
        error: (err) => console.error(err),
      });
  }
  // ==================== INITIALIZATION HELPERS ====================
  private _subscribeToAccountType(): void {
    this._accountTypeSub = this._userService.getAccountType().subscribe({
      next: (data) => {
        this.warehouseId = Number(localStorage.getItem('posId')) || null;
        this.accountType = data['user_type'];
        this._setAccountFlags();
        this._setWarehouseFlags();
        if (this.isProfile === 'true') {
          this.getUserOrders(1);
        } else {
          this._initializeNonProfileView();
        }
      },
      error: (error) => console.error(error),
    });
  }

  private _subscribeToUserId(): void {
    this._userIdSub = this._userService.getUserId().subscribe({
      next: (data) => {
        this.userId = Number(data['user_id']);
      },
      error: (error) => console.error(error),
    });
  }

  private _unsubscribeFromServices(): void {
    this._accountTypeSub?.unsubscribe();
    this._userIdSub?.unsubscribe();
  }

  private _initializeSelectedDeliveryOrders(): void {
    const stored = localStorage.getItem('selectedDeliveryOrders');
    this.selectedDeliveryOrders = stored ? JSON.parse(stored) : [];
  }

  private _setAccountFlags(): void {
    this.isAdmin = this.accountType === ACCOUNT_TYPE.ADMIN || this.accountType === ACCOUNT_TYPE.SUPER_ADMIN;
    this.isSubAdmin = this.accountType === ACCOUNT_TYPE.SUB_ADMIN;
    this.isDeliveryHero = this.accountType === ACCOUNT_TYPE.DELIVERY_HERO;
    this.showAdminContent = this.isAdmin || this.isSubAdmin;
  }

  private _setWarehouseFlags(): void {
    this.isCompany = false;
    this.isWarehouse = false;
    this.isStore = false;
    this.isClinic = false;
    this.isStoreAndClinic = false;

    if (!this.warehouseId) return;
    this._userService.getWarehouseType(this.warehouseId).subscribe({
      next: (data) => {
        const type = Number(data['warehouses_type']);
        switch (type) {
          case WAREHOUSE_TYPE.COMPANY: this.isCompany = true; break;
          case WAREHOUSE_TYPE.WAREHOUSE: this.isWarehouse = true; break;
          case WAREHOUSE_TYPE.STORE: this.isStore = true; break;
          case WAREHOUSE_TYPE.CLINIC: this.isClinic = true; break;
          case WAREHOUSE_TYPE.STORE_AND_CLINIC: this.isStoreAndClinic = true; break;
        }
      },
      error: (err) => console.error(err),
    });
  }

  get usesSimpleOrderStatusFilter(): boolean {
    return this.isStore || this.isClinic || this.isStoreAndClinic;
  }

  private _initializeNonProfileView(): void {
    this.displayedColumns.push('order_status', 'actions');
    if (!this.isAdmin && !this.isSubAdmin && !this.isDeliveryHero) {
      this.getUserOrders(1);
    } else if (this.isDeliveryHero) {
      this.displayedColumns.splice(8, 3);
      this.getDeliveryHeroOrders(localStorage.getItem('username')!, 1);
    } else {
      if (this.warehouseId != null) {
        this._itemsService.getWearHouses().subscribe({
          next: (res) => { this.allWareHouses = res.warehouse; },
          error: (err) => console.error(err),
        });
        this.getWarehouseOrdersData(this.warehouseId, 1);
      } else {
        this.noData = true;
        this.contentReady = false;
      }
    }
  }

  // ==================== REALTIME ORDERS (PUSH NOTIFICATIONS) ====================
  initRealtimeOrders(): void {
    if (!this.warehouseId) return;
    echo.channel(`orders-warehouse-${this.warehouseId}`)
      .listen('.order.created', (e: any) => {
        const newOrder = e.order;
        this._playAlertSound();
        this._showOrderAlert(e.order.id);
        if (!newOrder.governorate) newOrder.governorate = { governorate_name_ar: '' };
        if (!newOrder.city) newOrder.city = { city_name_ar: '' };
        this.allData.unshift(newOrder);
        this.dataSource.data = [...this.allData];
        this.cdr.detectChanges();
      });
  }

  private _playAlertSound(): void {
    this.notificationSound.loop = true;
    this.notificationSound.load();
    this.notificationSound.play().catch(err => console.error('Sound error:', err));
  }

  private _stopAlertSound(): void {
    this.notificationSound.pause();
    this.notificationSound.currentTime = 0;
  }

  private _showOrderAlert(orderId: any): void {
    this.notificationSound.loop = true;
    this.notificationSound.play().catch(() => console.log('Audio play deferred until user interaction.'));
    Swal.fire({
      icon: 'info',
      title: this.isAr ? 'طلب جديد! 🔔' : 'New Order! 🔔',
      text: this.isAr
        ? `وصل أوردر جديد برقم: ${orderId}`
        : `New order received with ID: ${orderId}`,
      confirmButtonText: this.isAr ? 'تم' : 'OK',
      allowOutsideClick: false,
      backdrop: `rgba(0,0,123,0.4)`
    }).then((result) => {
      if (result.isConfirmed) {
        this._stopAlertSound();
      }
    });
  }

  // ==================== DATA LOADING METHODS (PUBLIC - TEMPLATE BOUND) ====================
  getUserOrders(page: number): void {
    this.contentReady = true;
    this._userService.getUserId().subscribe({
      next: (data) => {
        this._orderService.getOrderByUserId(data['user_id'], page).subscribe({
          next: (data) => this._handleListApiResponse(data),
          error: (error) => this._handleApiError(error),
        });
      },
      error: (error) => this._handleApiError(error),
    });
  }

  getDeliveryHeroOrders(id: string, page: number): void {
    this.contentReady = true;
    this._orderService.getDeliveryHeroesOrders(id, page).subscribe({
      next: (data) => this._handleListApiResponse(data),
      error: (error) => this._handleApiError(error),
    });
  }

  getWarehouseOrdersData(id: number, perpage: number): void {
    this.searchstatus = null;
    this.contentReady = true;
    this._orderService.getOrdersByWarehouseId(id, perpage).subscribe({
      next: (data) => this._handleListApiResponse(data),
      error: (error) => this._handleApiError(error),
    });
  }

  getOrdersData(perpage: number, page: number): void {
    this.contentReady = true;
    this._orderService.getOrders(perpage, page).subscribe({
      next: (data) => this._handleListApiResponse(data),
      error: (error) => this._handleApiError(error),
    });
  }

  getTodaysOrders(ids: any): void {
    this.todaysOrdersIds = ids;
    this.showtodaysorders = false;
    this.contentReady = true;
    this._orderService.getOrdersByIds(ids).subscribe({
      next: (data) => {
        this.allData = data;
        this.dataSource = new MatTableDataSource(this.allData);
        this.contentReady = false;
      },
      error: (error) => this._handleApiError(error),
    });
  }

  getTodyasOrders(): void {
    this.showtodaysorders = true;
    this.contentReady = true;
    this._orderService.getTodayOrders().subscribe({
      next: (data) => {
        this.todaysOrders = data;
        this.contentReady = false;
      },
      error: (error) => this._handleApiError(error),
    });
  }

  // ==================== FILTERING & SEARCH ====================
  applyStatusFilter(id: number, statusType: string): void {
    this.resetPagination();
    this.filterbystatus(id, statusType, 1);
  }

  applyWareHouse(warehouseId: number): void {
    this.warehouseId = warehouseId;
    this.resetPagination();
    this.getWarehouseOrdersData(warehouseId, 1);
  }

  search(): void {
    this.resetPagination();
    if (!this.searchKey || this.searchKey.trim() === '') return;
    this.contentReady = true;
    this._orderService.searchOrders(
      this.searchKey,
      this.warehouseId,
      !this.isAdmin && !this.isSubAdmin ? this.userId : null
    ).subscribe({
      next: (data) => {
        this.allData = data;
        this.dataSource = new MatTableDataSource(this.allData);
        this.contentReady = false;
        this.noData = this.allData.length === 0;
      },
      error: (error) => this._handleApiError(error),
    });
  }

  searchOrderbyId(): void {
    this.resetPagination();
    this.contentReady = true;
    this._orderService.getOrderByWarehouse(this.warehouseId, this.searchOrderId).subscribe({
      next: (data) => {
        this.allData = [data];
        this.dataSource = new MatTableDataSource(this.allData);
        this.contentReady = false;
        this.noData = this.allData.length === 0;
      },
      error: () => {
        this.allData = [];
        this.dataSource = new MatTableDataSource(this.allData);
        this.noData = true;
      }
    });
  }

  filterbystatus(status: number, type: string, page: number): void {
    this.contentReady = true;
    this.searchstatus = status;
    this.searchtype = type;
    this.newPage = page;
    this._orderService.filterOrders(this.warehouseId, type, this.searchstatus, page, 10).subscribe({
      next: (data) => this._handleListApiResponse(data),
      error: (error) => this._handleApiError(error),
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.filter = filterValue;
  }

  // ==================== ORDER ACTIONS (PUBLIC - TEMPLATE BOUND) ====================
  deleteOrder(id: string, orderstatus: any): void {
    Swal.fire({
      title: this.isAr ? 'متأكد؟؟' : 'Are You Sure?',
      showCancelButton: true,
      confirmButtonText: this.isAr ? 'اه' : 'YES',
      cancelButtonText: this.isAr ? 'لا' : 'no'
    }).then((result) => {
      if (result.isConfirmed) {
        if (orderstatus != '2') {
          this.contentReady = true;
          this._orderService.updateQty('3', id).subscribe({
            next: () => {
              this._orderService.deleteOrder(id).subscribe({
                next: () => this._refreshOrdersData(),
                error: (err) => console.error(err),
              });
            },
            error: (err) => console.error(err),
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: this.isAr ? 'للاسف' : 'sorry',
            text: this.isAr ? 'لازم تبقي ادمن' : 'can`t delete this order contact the admin',
          });
        }
      }
    });
  }

  changeorderstatus(orderId: number, status: string, reason?: string, total?: number): void {
    this.adminStatus = status;
    this.commentOrderId = orderId;
    this.commentTotal = total;
    Swal.fire({
      title: this.isAr ? 'عايز تسيب تعليق' : 'add comment?',
      showCancelButton: true,
      confirmButtonText: this.isAr ? 'اه' : 'YES',
      cancelButtonText: this.isAr ? 'لا' : 'no'
    }).then((result) => {
      if (result.isConfirmed) {
        this.showCommentPopUp = true;
      } else {
        this._executeOrderStatusChange(orderId, status, reason, total);
      }
    });
  }

  updateComment(): void {
    if (!this.commentOrderId) return;
    this.contentReady = true;
    this._orderService.addCommentInOrder(this.commentOrderId, this.comment || '').subscribe({
      next: () => {
        this._executeOrderStatusChange(this.commentOrderId, this.adminStatus, "", this.commentTotal);
        this.showCommentPopUp = false;
        this.comment = '';
      },
      error: (err) => console.error(err),
    });
  }

  rejectorder(orderId: number): void {
    this.orderId = orderId;
    this.showRejectPopUp = true;
  }

  onSubmit(): void {
    if (this.orderId) {
      this._executeOrderStatusChange(this.orderId, '3', this.reason);
    }
    this.close();
  }

  addDiscount(): void {
    if (!this.DiscountOrderId || this.total == null || this.discount == null) return;
    this.contentReady = true;
    const newTotal = parseInt(String(this.total), 10) - parseInt(this.discount, 10);
    this._orderService.adddiscountOrTotalOrIcome(this.DiscountOrderId, String(newTotal), this.income, this.discount).subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: this.isAr ? 'تمام' : 'success',
          text: this.isAr ? 'تم اضافة خصم' : 'discount added',
        });
        this.DiscountOrderId = null;
        this.showDiscountPopUp = false;
        this.contentReady = false;
        this._refreshOrdersData();
      },
    });
  }

  addHero(): void {
    if (!this.deliveryHeroOrderId) return;
    this.contentReady = true;
    this._orderService.addDeliveryHero(this.deliveryHeroOrderId, this.deliveryHeroInfo).subscribe({
      next: () => {
        this.contentReady = false;
        this.showDeliveryHeroPopUp = false;
        this._addHeroId();
      },
    });
  }

  changeDeliveryStatus(orderId: number, status: number, warehouseId: any, total?: number): void {
    this.contentReady = true;
    this._orderService.updateDeliveryStatus(orderId, status).subscribe({
      next: () => {
        this.getDeliveryHeroOrders(localStorage.getItem('username')!, 1);
        Swal.fire({
          icon: 'success',
          title: this.isAr ? 'تمام' : 'success',
          text: this.isAr ? 'تم تحديث حالة التوصيل' : 'Delivery status updated',
        });
        if (status === DELIVERY_STATUS.DELIVERED) {
          this.sendToFinancialManager(orderId, localStorage.getItem('username'), localStorage.getItem('username'), total, warehouseId);
        }
        this.contentReady = false;
      },
    });
  }

  partialComplated(): void {
    if (!this.orderId) return;
    this.contentReady = true;
    let totalSum = 0;
    let totalSumRealPricesNumber = 0;
    this.selectedProductsDetails.forEach((item: any) => {
      totalSumRealPricesNumber += item.itemPrice * item.quantity;
      totalSum += item.price * item.quantity;
    });
    const updatedQuantities = this.selectedProductsDetails.map((item: any) => Number(item.quantity));
    const updatedBonuses = this.selectedProductsDetails.map((item: any) => Number(item.bounes));
    this.total = totalSum + parseInt(String(this.deliveryFees), 10);
    this.duenow = this.total - totalSumRealPricesNumber - parseInt(String(this.deliveryFees), 10);
    this._orderService
      .updateQuantityIncomeTotal(
        this.orderId,
        updatedQuantities,
        updatedBonuses,
        String(this.duenow),
        String(this.total)
      )
      .subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: this.isAr ? 'تمام' : 'success',
            text: this.isAr ? 'تم تعديل الفاتورة' : 'invoice updated',
          });
          if (!this.isWarehouse) this.showPaymentPopUp = true;
          this.showPartCompPopUp = false;
          this.contentReady = false;
          this._refreshOrdersData();
        },
        error: (err) => console.error('خطأ في التحديث', err),
      });
  }

  changePaymentType(): void {
    if (!this.orderId) return;
    this.contentReady = true;
    const updatePayload = {
      orderId: this.orderId,
      payment_type: this.payment_type,
      paid: this.payment_type === PAYMENT_TYPE.CREDIT ? this.payed : undefined,
      remaining: this.payment_type === PAYMENT_TYPE.CREDIT ? this.not_payed : undefined,
      isChangeRemaining: this.changeRemaining
    };
    const apiCall = updatePayload.isChangeRemaining
      ? this._orderService.updatePaymentType(updatePayload.orderId, undefined, this.payed, this.not_payed)
      : this._orderService.updatePaymentType(updatePayload.orderId, updatePayload.payment_type, updatePayload.paid, updatePayload.remaining);
    apiCall.subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: this.isAr ? 'تمام' : 'Success',
          text: this.isAr
            ? (updatePayload.isChangeRemaining ? 'تم تعديل المبلغ' : 'تم تعديل الدفع')
            : (updatePayload.isChangeRemaining ? 'Amount updated' : 'Payment updated'),
        });
        const revAmount = (updatePayload.isChangeRemaining || this.payment_type === PAYMENT_TYPE.CREDIT) ? this.payed : this.total;
        this.addRev(revAmount, this.orderId, this.payment_type);
        this.showPaymentPopUp = false;
        this.payment_type = null;
        this.changeRemaining = false;
        this.contentReady = false;
        this._refreshOrdersData();
      },
      error: () => {
        Swal.fire({
          icon: 'error',
          title: this.isAr ? 'خطأ' : 'Error',
          text: this.isAr
            ? (updatePayload.isChangeRemaining ? 'فشل في تعديل المبلغ' : 'فشل في تعديل الدفع')
            : (updatePayload.isChangeRemaining ? 'Failed to update amount' : 'Failed to update payment'),
        });
        this.contentReady = false;
      }
    });
  }

  // ==================== UI & POPUP HELPERS (PUBLIC - TEMPLATE BOUND) ====================
  close(): void {
    this.showQuickInvoice = false;
    this.showPopUp = false;
    this.showRejectPopUp = false;
    this.showCommentPopUp = false;
    this.showDiscountPopUp = false;
    this.showDeliveryHeroPopUp = false;
    this.showPartCompPopUp = false;
    this.showPaymentPopUp = false;
    this.showtodaysorders = false;
  }

  getUserItems(id: string): void {
    this.contentReady = true;
    this._profileService.getuserinfo(id).subscribe({
      next: (res) => {
        this.userData = res;
        this.showPopUp = true;
        this.userName = this.userData.username;
        this.userfirstname = this.userData.first_name;
        this.userFacebook = this.userData.facebook_link;
        this.userInstgram = this.userData.instagram_link;
        this.userTiktok = this.userData.tiktok_link;
        this.userPhone = this.userData.phone;
        this.userimage = 'https://backendproduction.blueboxpet.com/   ' + this.userData.image;
        this.contentReady = false;
      },
    });
  }

  quickInvoice(orderId: number): void {
    this.selectedOrderId = orderId;
    this.showQuickInvoice = true;
  }

  showDiscount(orderId: number, total: number): void {
    Swal.fire({
      title: this.isAr ? 'عايز تعمل خصم علي الفاتورة' : 'add Discount?',
      showCancelButton: true,
      confirmButtonText: this.isAr ? 'اه' : 'yes',
      cancelButtonText: this.isAr ? 'لا' : 'no'
    }).then((result) => {
      if (result.isConfirmed) {
        this.DiscountOrderId = orderId;
        this.total = total;
        this.showDiscountPopUp = true;
      }
    });
  }

  onDeliveryHeroChange(): void {
    if (this.deliveryHero) {
      this.deliveryHeroId = this.deliveryHero.phone;
      this.deliveryHeroInfo = `${this.deliveryHero.name} ${this.deliveryHero.phone}`;
    } else {
      console.log('No object selected.');
    }
  }

  showPopUpPartiallyCompleted(order_Obj: any): void {
    this.showPartCompPopUp = true;
    this.orderId = order_Obj.id;
    this.total = order_Obj.total;
    this.duenow = 'مكتمل جزئيا';
    this.deliveryFees = order_Obj.delivery_fees;
    this.income = order_Obj.income;
    const data = {
      price: order_Obj.price,
      item: order_Obj.item[0].split(','),
      product_id: order_Obj.product_id,
      quantity: order_Obj.quantity,
      itemPrice: order_Obj.itemsprices[0].split(','),
      bounes: order_Obj.bonuses
    };
    this.selectedProductsDetails = data.item.map((itm: string, i: number) => ({
      product_id: data.product_id[i],
      item: itm.trim(),
      price: data.price[i],
      bounes: data.bounes[i],
      quantity: data.quantity[i],
      itemPrice: data.itemPrice[i]
    }));
  }

  addremaining(paid: any, remaining: any, id: number): void {
    this.orderId = id;
    this.payed = paid;
    this.not_payed = remaining;
    this.changeRemaining = true;
    this.showPaymentPopUp = true;
  }

  advancedSearch(): void {
    this.showoptions = true;
  }

  clearFilters(): void {
    this.showoptions = false;
  }

  addToDeliveryOrders(order: any): void {
    if (!this.selectedDeliveryOrders) {
      this.selectedDeliveryOrders = [];
    }
    const exists = this.selectedDeliveryOrders.some((o) => o.id === order.id);
    if (!exists) {
      this.selectedDeliveryOrders.push(order);
      localStorage.setItem('selectedDeliveryOrders', JSON.stringify(this.selectedDeliveryOrders));
    } else {
      Swal.fire({
        icon: 'error',
        title: this.isAr ? 'اضاف قبل كده' : 'Order already exists',
        confirmButtonText: this.isAr ? 'تمام' : 'ok',
      });
    }
  }

  // ==================== NAVIGATION & UTILS (PUBLIC - TEMPLATE BOUND) ====================
  details(orderId: number, website: boolean): void {
    const posId = Number(localStorage.getItem('posId'));

    this._userService.getWarehouseType(posId).subscribe({
      next: ({ warehouses_type }) => {
        const type = Number(warehouses_type);

        // 🔥 Navigation based on type
        if ([2, 3, 4].includes(type)) {
          this.router.navigate(['/storeinvoice'], {
            queryParams: { id: orderId }
          });
          return;
        }

        else if (type === 1) {
          this.router.navigate(['/warehouseinvoice'], {
            queryParams: { id: orderId }
          });
          return;
        }

        else {
          if (website) {
            this.router.navigate(['/websiteinvoice'], {
              queryParams: { id: orderId }
            });
          }
          else {
            this.router.navigate(['/petcompanyinvoice'], {
              queryParams: { id: orderId }
            });
          }
          return;
        }
      },
      error: (err) => console.error(err),
    });
  }
  accStatement(phone: string): void {
    this.router.navigate(['/accountstatement'], { queryParams: { phone: phone } });
  }

  edit(itemobj: any): void {
    this.router.navigate(['/Addorder'], { queryParams: itemobj });
  }

  exportToExcel(): void {
    this.excelExportService.exportToExcel('orderstable', 'Orders', ['actions']);
  }

  resetPagination(): void {
    if (this.paginator) this.paginator.firstPage();
    this.newPage = 1;
  }

  onPaginateChange(event: PageEvent): void {
    this.clear = false;
    this.showoptions = false;
    this.todaysOrdersIds = null;
    this.newPage = event.pageIndex + 1;
    if (this.searchstatus != null) {
      this.filterbystatus(this.searchstatus, this.searchtype || '', this.newPage);
    } else {
      this._refreshOrdersData();
    }
  }

  trackById(index: number, item: any): any {
    return item?.id;
  }

  convertDate(dateString: string): string {
    const date = new Date(dateString);
    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  tolist(list: string | number): string[] {
    return String(list).split(',').map(item => item.trim());
  }

  // ==================== BUSINESS LOGIC HELPERS ====================
  requestYourCommission(orderId: number): void {
    this._orderService.paymentRequest(orderId).subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: this.isAr ? 'تمام' : 'success',
          text: this.isAr ? 'تم طلب عمولتك' : 'your Commission request added',
        });
      },
      error: (err) => console.error(err),
    });
  }

  getAllHeros(warehouse_id: number, hasPagination: boolean): void {
    this._deliveryhero.getAllDeliveryStars(warehouse_id, hasPagination).subscribe({
      next: (data) => {
        this.deliveryHeros = data;
      },
      error: (error) => console.error(error),
    });
  }

  addDeliveryHero(orderId: number): void {
    if (!this.warehouseId) return;
    this.getAllHeros(this.warehouseId, false);
    this.deliveryHeroOrderId = orderId;
    this.showDeliveryHeroPopUp = true;
  }

  private _addHeroId(): void {
    if (!this.deliveryHeroOrderId || !this.deliveryHeroId) return;
    this._orderService.updateDeliveryHero(this.deliveryHeroOrderId, this.deliveryHeroId).subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: this.isAr ? 'تمام' : 'success',
          text: this.isAr ? 'تم اصافة مندوب شحن' : 'delivery Hero added',
        });
        this.deliveryHeroOrderId = null;
        this.deliveryHeroId = null;
        this.deliveryHeroInfo = null;
        this._refreshOrdersData();
      },
    });
  }

  sendToFinancialManager(orderId: any, sub_dilivery_id: any, deliveryhero_id: any, amount_collected: any, warehouseId: any): void {
    this.contentReady = true;
    const formData = new FormData();
    formData.append('order_id', String(orderId));
    formData.append('sub_dilivery_id', String(sub_dilivery_id));
    formData.append('deliveryhero_id', String(deliveryhero_id));
    formData.append('amount_collected', String(amount_collected));
    formData.append('warehouse_id', String(warehouseId));
    this._deliveryhero.createDeliveryCollection(formData).subscribe({
      next: () => { this.contentReady = false; },
      error: (err) => {
        console.error('Error:', err);
        this.contentReady = false;
      }
    });
  }

  addRev(total: any, orderid: any, type?: string): void {
    this.contentReady = true;
    const bonusPayload = {
      warehouse_id: localStorage.getItem('posId'),
      employee_id: this.userId,
      username: localStorage.getItem('username'),
      amount: total,
      amount_type: type ?? '0',
      note: String(orderid),
      type: '3',
    };
    this._financialManager.addRevenue(bonusPayload).subscribe({
      next: () => { this.contentReady = false; },
      error: (error) => {
        console.error(error);
        this.contentReady = false;
      },
    });
  }

  updateQuantity(status: string, orderId: string): void {
    this._orderService.updateQty(status, orderId).subscribe({
      next: (res) => console.log(res),
      error: (err) => console.error(err),
    });
  }

  // ==================== PRIVATE CORE HELPERS (SAFE REFACTORING) ====================
  private _executeOrderStatusChange(orderId: number, status: string, reason?: string, total?: number): void {
    if (!orderId) return;
    this.contentReady = true;
    this.total = total;
    this.orderId = orderId;
    this._orderService.changeStatus(orderId, status, reason).subscribe({
      next: () => {
        if (status === '3') this.updateQuantity(status, String(orderId));
        if (status === '5' && !this.isWarehouse) this.showPaymentPopUp = true;
        this.reason = '';
        this._refreshOrdersData();
      },
      error: (err) => this._handleApiError(err),
    });
  }

  private _refreshOrdersData(): void {
    if (this.searchstatus != null) {
      this.filterbystatus(this.searchstatus, this.searchtype || '', this.newPage);
    } else if (this.isDeliveryHero) {
      this.getDeliveryHeroOrders(localStorage.getItem('username')!, this.newPage);
    } else if (this.todaysOrdersIds != null) {
      this.getTodaysOrders(this.todaysOrdersIds);
    } else if (this.isAdmin || this.isSubAdmin) {
      if (this.warehouseId) {
        this.getWarehouseOrdersData(this.warehouseId, this.newPage);
      }
    } else {
      this.getUserOrders(this.newPage);
    }
  }

  private _handleListApiResponse(data: any): void {
    this.allData = data['data'] || data;
    this.stockTotal = data['total'] || 0;
    this.totalItems = data['last_page'] || 0;
    this.dataSource = new MatTableDataSource(this.allData);
    this.contentReady = false;
    this.noData = this.allData.length === 0;
  }

  private _handleApiError(error: any): void {
    console.error(error);
    this.contentReady = false;
  }
}
